import React from "react"
import { Link, useLocation } from "react-router-dom"
import {
  Building2,
  Calendar,
  Camera,
  ChevronDown,
  ChevronRight,
  Home,
  Lock,
  Package,
  Search,
  Settings,
} from "lucide-react"
import { useAuth } from "../context/AuthContext"
import { useSidebar } from "../context/SidebarContext"
import { THEME } from "../config/theme"

const MOBILE_BREAKPOINT = 768

type IconComponent = React.ComponentType<{ size?: number; strokeWidth?: number }>

// A NavChild is either a leaf route (has `path`) or a sub-group with its own
// children (has `sectionId` + `children`). Recursive shape lets sections like
// Inventory > Wholesale > {Adesa Cleared List, Reports, Preferences} render
// as a third level beneath the existing two.
interface NavChild {
  label: string
  path?: string
  sectionId?: string
  children?: NavChild[]
  requiresPermission?: string
}

interface NavSection {
  icon: IconComponent
  label: string
  sectionId?: string
  path?: string
  children?: NavChild[]
  requiresPermission?: string
}

const NAV_SECTIONS: NavSection[] = [
  { icon: Search,   label: "Search",            path: "/search" },
  { icon: Home,     label: "Home",              path: "/dashboard" },
  // INVI HQ — the platform operator's cockpit for running the business,
  // separate from the dealer-facing tools. System-admin only (view_insights).
  {
    icon: Building2,
    label: "INVI HQ",
    sectionId: "invi_hq",
    requiresPermission: "view_insights",
    children: [
      { label: "Insights", path: "/hq/insights" },
      { label: "Scheduled Reports", path: "/hq/scheduled-reports" },
      { label: "Announcements", path: "/hq/announcements" },
      { label: "Photo AI", path: "/hq/photo-ai" },
      // Platform data operations. System-admin-only via the INVI HQ section gate.
      // Integrations already covers feed status + import/run history, so those
      // aren't separate items. The rest are placeholders = build roadmap.
      {
        label: "Data",
        sectionId: "hq_data",
        children: [
          { label: "Integrations",      path: "/admin/integrations" },
          { label: "Data Quality",      path: "/hq/data/quality" },
          { label: "Schema & Mappings", path: "/hq/data/schema" },
          { label: "Retention",         path: "/hq/data/retention" },
          { label: "Backups",           path: "/hq/data/backups" },
        ],
      },
    ],
  },
  {
    icon: Package,
    label: "Inventory",
    sectionId: "inventory",
    children: [
      { label: "Vehicle Inventory", path: "/inventory/vehicles" },
      { label: "Inventory Audit", path: "/inventory/audit" },
      {
        label: "Wholesale",
        sectionId: "inventory_wholesale",
        requiresPermission: "view_wholesale",
        children: [
          { label: "Adesa Cleared List", path: "/inventory/wholesale/adesa-cleared-list" },
          { label: "Adesa Run List",     path: "/inventory/wholesale/adesa-run-list" },
          { label: "Reports",            path: "/inventory/wholesale/reports" },
          { label: "Preferences",        path: "/inventory/wholesale/preferences" },
        ],
      },
    ],
  },
  {
    icon: Camera,
    label: "Photos",
    sectionId: "photos",
    children: [
      { label: "Overview",         path: "/photos/overview",         requiresPermission: "view_image_index" },
      { label: "Image Index",      path: "/photos/image-index",      requiresPermission: "view_image_index" },
      { label: "Daily Photo List", path: "/photos/daily" },
      { label: "Quality Control",  path: "/photos/quality-control",  requiresPermission: "view_image_index" },
      { label: "Configuration",    path: "/photos/configuration",    requiresPermission: "manage_photo_configuration" },
    ],
  },
  // The dealer's scheduled emails — today the morning audit alerts. Same
  // permission as the page, so nobody sees a link to a page they cannot use.
  { icon: Calendar, label: "Scheduled Reports", path: "/reports/scheduled", requiresPermission: "manage_audit_alerts" },
  {
    icon: Settings,
    label: "Settings",
    sectionId: "settings",
    children: [{ label: "My Account", path: "/settings/account" }],
  },
  {
    icon: Lock,
    label: "Admin",
    sectionId: "admin",
    requiresPermission: "manage_users",
    children: [
      { label: "User Management", path: "/admin/users" },
    ],
  },
]

const Sidebar: React.FC = () => {
  const location = useLocation()
  const { hasPermission } = useAuth()
  const { isCollapsed, setCollapsed, isSectionExpanded, toggleSection } = useSidebar()

  const isActive = (path: string) => location.pathname === path

  const handleLinkClick = () => {
    if (typeof window !== "undefined" && window.innerWidth < MOBILE_BREAKPOINT) {
      setCollapsed(true)
    }
  }

  const isMobile =
    typeof window !== "undefined" && window.innerWidth < MOBILE_BREAKPOINT

  // 10% navy (#0D1A42) tinted against white surface — used as the active
  // wash so the brand accent registers without overwhelming the sidebar.
  const ACTIVE_BG = "rgb(231, 232, 236)"

  const topItemStyle = (active: boolean): React.CSSProperties => ({
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "10px 16px",
    fontSize: "14px",
    color: active
      ? THEME.colors.navy
      : THEME.colors.neutral.light.textPrimary,
    cursor: "pointer",
    fontFamily: THEME.fonts.primary,
    textDecoration: "none",
    background: active ? ACTIVE_BG : "transparent",
    borderLeft: active
      ? `3px solid ${THEME.colors.navy}`
      : "3px solid transparent",
    fontWeight: active ? 600 : 400,
    userSelect: "none",
  })

  const childItemStyle = (active: boolean): React.CSSProperties => ({
    display: "flex",
    alignItems: "center",
    padding: "8px 16px 8px 32px",
    fontSize: "13px",
    color: active
      ? THEME.colors.navy
      : THEME.colors.neutral.light.textPrimary,
    cursor: "pointer",
    fontFamily: THEME.fonts.primary,
    textDecoration: "none",
    background: active ? ACTIVE_BG : "transparent",
    borderLeft: active
      ? `3px solid ${THEME.colors.navy}`
      : "3px solid transparent",
    fontWeight: active ? 600 : 400,
  })

  // Third-level rows (e.g. Inventory > Wholesale > items). Same as
  // childItemStyle but indented further to make the hierarchy visible.
  const grandchildItemStyle = (active: boolean): React.CSSProperties => ({
    ...childItemStyle(active),
    padding: "8px 16px 8px 48px",
  })

  const sidebarWidth = THEME.sidebar.width
  const transition = `transform ${THEME.sidebar.transitionDuration} ${THEME.sidebar.transitionEasing}`

  return (
    <nav
      style={{
        width: `${sidebarWidth}px`,
        minWidth: `${sidebarWidth}px`,
        // Fixed to the viewport (minus the 48px top bar) and scrollable on its own,
        // so a fully-expanded menu can always reach its bottom items even when the
        // page content is too short to scroll the shell.
        height: "calc(100vh - 48px)",
        overflowY: "auto",
        background: THEME.colors.neutral.light.surface,
        borderRight: `1px solid ${THEME.colors.neutral.light.border}`,
        paddingTop: "16px",
        paddingBottom: "16px",
        fontFamily: THEME.fonts.primary,
        boxSizing: "border-box",
        transform: isCollapsed ? "translateX(-100%)" : "translateX(0)",
        transition,
        ...(isMobile
          ? {
              position: "fixed" as const,
              top: 48,
              left: 0,
              zIndex: 90,
              boxShadow: isCollapsed
                ? "none"
                : "2px 0 8px rgba(0,0,0,0.1)",
            }
          : {
              position: "relative" as const,
              marginLeft: isCollapsed ? `-${sidebarWidth}px` : "0px",
            }),
      }}
    >
      {NAV_SECTIONS.map((section) => {
        if (section.requiresPermission && !hasPermission(section.requiresPermission)) {
          return null
        }

        const Icon = section.icon

        if (section.path && !section.children) {
          return (
            <Link
              key={section.label}
              to={section.path}
              style={topItemStyle(isActive(section.path))}
              onClick={handleLinkClick}
            >
              <Icon size={16} strokeWidth={1.75} />
              <span>{section.label}</span>
            </Link>
          )
        }

        const sId = section.sectionId!
        const open = isSectionExpanded(sId)
        const anyChildActive = !!section.children?.some((c) =>
          childContainsActiveRoute(c, isActive),
        )
        const headerActive = anyChildActive && !open

        return (
          <div key={section.label}>
            <div
              onClick={() => toggleSection(sId)}
              style={{
                ...topItemStyle(headerActive),
                justifyContent: "space-between",
              }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <Icon size={16} strokeWidth={1.75} />
                <span>{section.label}</span>
              </span>
              {open ? (
                <ChevronDown size={14} color={THEME.colors.neutral.light.textSecondary} strokeWidth={1.75} />
              ) : (
                <ChevronRight size={14} color={THEME.colors.neutral.light.textSecondary} strokeWidth={1.75} />
              )}
            </div>
            {open &&
              section.children?.map((child) =>
                renderChild(child, {
                  isActive,
                  hasPermission,
                  isSectionExpanded,
                  toggleSection,
                  handleLinkClick,
                  childItemStyle,
                  grandchildItemStyle,
                  textSecondary: THEME.colors.neutral.light.textSecondary,
                }),
              )}
          </div>
        )
      })}
    </nav>
  )
}

// Returns true when `child` (a leaf or sub-group) contains the currently
// active route, recursively.
function childContainsActiveRoute(
  child: NavChild,
  isActive: (path: string) => boolean,
): boolean {
  if (child.path) return isActive(child.path)
  return !!child.children?.some((c) => childContainsActiveRoute(c, isActive))
}

interface RenderChildContext {
  isActive: (path: string) => boolean
  hasPermission: (permission: string) => boolean
  isSectionExpanded: (id: string) => boolean
  toggleSection: (id: string) => void
  handleLinkClick: () => void
  childItemStyle: (active: boolean) => React.CSSProperties
  grandchildItemStyle: (active: boolean) => React.CSSProperties
  textSecondary: string
}

function renderChild(child: NavChild, ctx: RenderChildContext): React.ReactNode {
  if (child.requiresPermission && !ctx.hasPermission(child.requiresPermission)) {
    return null
  }

  // Leaf \u2014 direct route.
  if (child.path) {
    return (
      <Link
        key={child.path}
        to={child.path}
        style={ctx.childItemStyle(ctx.isActive(child.path))}
        onClick={ctx.handleLinkClick}
      >
        {child.label}
      </Link>
    )
  }

  // Sub-group \u2014 render its own header + grandchildren beneath.
  const sId = child.sectionId!
  const open = ctx.isSectionExpanded(sId)
  const anyGrandchildActive = !!child.children?.some((g) =>
    childContainsActiveRoute(g, ctx.isActive),
  )
  const headerActive = anyGrandchildActive && !open

  return (
    <div key={child.label}>
      <div
        onClick={() => ctx.toggleSection(sId)}
        style={{
          ...ctx.childItemStyle(headerActive),
          justifyContent: "space-between",
        }}
      >
        <span>{child.label}</span>
        {open ? (
          <ChevronDown size={14} color={ctx.textSecondary} strokeWidth={1.75} />
        ) : (
          <ChevronRight size={14} color={ctx.textSecondary} strokeWidth={1.75} />
        )}
      </div>
      {open &&
        child.children?.map((grand) => {
          if (grand.requiresPermission && !ctx.hasPermission(grand.requiresPermission)) {
            return null
          }
          if (!grand.path) return null
          return (
            <Link
              key={grand.path}
              to={grand.path}
              style={ctx.grandchildItemStyle(ctx.isActive(grand.path))}
              onClick={ctx.handleLinkClick}
            >
              {grand.label}
            </Link>
          )
        })}
    </div>
  )
}

export default Sidebar
